import type { AudienceBand, PlanMatrix } from "@shared/plan-matrix";
import type {
  Device,
  GenreCount,
  Lang,
  ListItem,
  MyTitles,
  PortalContext,
  Profile,
  StoreRail,
  TitleDetail,
  TitleSort,
  TitlesPage,
  OrgPlanView,
  Viewer,
} from "./types";
import type {
  InformationRail,
  WebEpisodesResponse,
  WebHeartbeatResponse,
  WebLicenceResponse,
  WebPlayResponse,
} from "@shared/consumer-tvod";

/**
 * The purchase portal's API client.
 *
 * ## Same origin, host-only cookies
 *
 * Every call goes to `/api/interactiveplayer/v1/portal/*` on the host the page was loaded from.
 * The edge Worker forwards it to the API with the browser's host, and **the
 * host is the tenant**: nothing here names a provider. The session lives in
 * two httpOnly cookies the API sets without a `Domain=`, so a session from
 * one provider's portal is never sent to another. `credentials:
 * "same-origin"` is stated on every call rather than relied on as a default.
 *
 * ## Refresh
 *
 * The access cookie lives 15 minutes; the refresh cookie (path
 * `/api/interactiveplayer/v1/portal/auth`) rotates on every use and a REUSED refresh token revokes
 * the session (`docs/design/consumer-tvod-app-api.md`: refreshes must be
 * serial). So a 401 on a signed-in route triggers at most ONE refresh at a
 * time - shared by every caller in this tab, and across tabs through the Web
 * Locks API where the browser has it - and the request is retried once.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown> = {}
  ) {
    super(code);
    this.name = "ApiError";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

const BASE = "/api/interactiveplayer/v1/portal";

async function errorOf(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as Record<string, unknown>;
    const code = typeof body.error === "string" ? body.error : "";
    return new ApiError(res.status, code || `http_${res.status}`, body);
  } catch {
    return new ApiError(res.status, `http_${res.status}`);
  }
}

async function raw(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<Response> {
  return fetch(BASE + path, {
    method,
    signal,
    credentials: "same-origin",
    headers:
      body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/* ── single-flight refresh ─────────────────────────────────────────────── */

let refreshing: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const res = await raw("POST", "/auth/refresh").catch(() => null);
  return !!res && res.ok;
}

/**
 * Rotate the refresh cookie. True when a fresh access cookie is now set.
 * Concurrent callers share one request; other tabs wait on a Web Lock.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const locks = (
    navigator as unknown as {
      locks?: {
        request(name: string, cb: () => Promise<boolean>): Promise<boolean>;
      };
    }
  ).locks;
  const run: Promise<boolean> = locks
    ? locks.request("foyer-portal-refresh", doRefresh)
    : doRefresh();
  const shared = run.finally(() => {
    refreshing = null;
  });
  refreshing = shared;
  return shared;
}

const SESSION_CODES = new Set(["session_expired", "session_revoked"]);

async function call<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts: { retry?: boolean; signal?: AbortSignal } = {}
): Promise<T> {
  let res = await raw(method, path, body, opts.signal);
  if (res.status === 401 && opts.retry !== false) {
    const err = await errorOf(res.clone());
    if (SESSION_CODES.has(err.code) && (await refreshSession())) {
      res = await raw(method, path, body, opts.signal);
    }
  }
  if (!res.ok) throw await errorOf(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
    );
  return parts.length ? `?${parts.join("&")}` : "";
}

/** No refresh-and-retry: these answer for themselves (wrong password = 401). */
const noRetry = { retry: false };

export const portalApi = {
  /* ── the Information rail (members only) ── */

  /** 401 for a visitor: the rail is for signed-in members. */
  information(signal?: AbortSignal): Promise<InformationRail> {
    return call<InformationRail>("GET", "/information", undefined, { signal });
  },

  /**
   * An Information file's bytes, whole - what PDF.js reads. Through the same
   * refresh-and-retry as every call: a member who reads for 15 minutes has
   * an expired access cookie by the next document.
   */
  async informationBytes(
    fileUrl: string,
    signal?: AbortSignal
  ): Promise<ArrayBuffer> {
    if (!fileUrl.startsWith(`${BASE}/information/`)) {
      throw new ApiError(400, "invalid_request");
    }
    const path = fileUrl.slice(BASE.length);
    let res = await raw("GET", path, undefined, signal);
    if (res.status === 401) {
      const err = await errorOf(res.clone());
      if (SESSION_CODES.has(err.code) && (await refreshSession())) {
        res = await raw("GET", path, undefined, signal);
      }
    }
    if (!res.ok) throw await errorOf(res);
    return res.arrayBuffer();
  },

  /* ── bootstrap ── */

  /** 404 `not_found` = this host is not a live portal (whatever the reason). */
  context(): Promise<PortalContext> {
    return call<PortalContext>("GET", "/context", undefined, noRetry);
  },

  /* ── account ── */

  /** Closed registration, step 1: an organisation applies (always 202). */
  apply(body: {
    companyName: string;
    orgNumber: string;
    attention: string;
    street: string;
    postcode: string;
    city: string;
    contactEmail: string;
    band: AudienceBand;
    levelId: string;
    locale: Lang;
  }): Promise<{ ok: true }> {
    return call("POST", "/applications", body, noRetry);
  },
  /** The purchaser's plan, picks and upgrade prices (ORG-5). */
  org(): Promise<OrgPlanView> {
    return call("GET", "/org");
  },
  orgPick(assetIds: string[]): Promise<OrgPlanView> {
    return call("POST", "/org/picks", { assetIds }, noRetry);
  },
  orgUpgrade(band: AudienceBand, levelId: string): Promise<OrgPlanView> {
    return call("POST", "/org/upgrade", { band, levelId }, noRetry);
  },
  /** The organisation's play accounts (ORG-6). */
  orgViewers(): Promise<{ viewers: Viewer[] }> {
    return call("GET", "/org/viewers");
  },
  orgShare(email: string): Promise<{ viewer: Viewer; mailed: boolean }> {
    return call("POST", "/org/viewers", { email }, noRetry);
  },
  orgRevoke(viewerId: string): Promise<{ ok: true }> {
    return call(
      "DELETE",
      `/org/viewers/${encodeURIComponent(viewerId)}`,
      undefined,
      noRetry
    );
  },
  /** What the provider sells organisations (only sold levels). */
  plans(): Promise<PlanMatrix> {
    return call("GET", "/plans");
  },
  /** Swedish postcode → town; null when the code is unknown. */
  async postcode(code: string): Promise<{ city: string } | null> {
    try {
      return await call<{ city: string }>("GET", `/postcodes/${code}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  confirm(token: string): Promise<{ ok: true }> {
    return call("POST", "/auth/confirm", { token }, noRetry);
  },
  resendConfirmation(email: string): Promise<{ ok: true }> {
    return call("POST", "/auth/confirm/resend", { email }, noRetry);
  },
  login(body: {
    email: string;
    password: string;
    deviceName?: string;
  }): Promise<{ account: Profile; accessTokenExpiresAt: string }> {
    return call("POST", "/auth/login", body, noRetry);
  },
  logout(): Promise<void> {
    return call("POST", "/auth/logout", undefined, noRetry);
  },
  logoutEverywhere(): Promise<void> {
    return call("POST", "/auth/logout-everywhere");
  },
  forgotPassword(email: string): Promise<{ ok: true }> {
    return call("POST", "/auth/password/forgot", { email }, noRetry);
  },
  resetPassword(token: string, password: string): Promise<{ ok: true }> {
    return call("POST", "/auth/password/reset", { token, password }, noRetry);
  },
  me(): Promise<Profile> {
    return call<Profile>("GET", "/me");
  },
  updateProfile(body: {
    displayName?: string;
    locale?: Lang;
    marketingOptIn?: boolean;
    email?: string;
    currentPassword?: string;
  }): Promise<Profile & { emailChangePending: boolean }> {
    return call("PATCH", "/me", body);
  },
  changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ ok: true }> {
    return call("POST", "/me/password", { currentPassword, newPassword });
  },
  devices(): Promise<{ devices: Device[] }> {
    return call("GET", "/me/devices");
  },
  signOutDevice(sessionId: string): Promise<void> {
    return call("DELETE", `/me/devices/${encodeURIComponent(sessionId)}`);
  },
  /** The GDPR export, as a Blob for the browser to save. */
  async exportData(): Promise<Blob> {
    const data = await call<unknown>("GET", "/me/export");
    return new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
  },
  deleteAccount(password: string): Promise<void> {
    return call("DELETE", "/me", { password });
  },

  /* ── catalogue ── */

  /**
   * A page of the store's titles (the per-portal index): `genre`, `q`
   * (≥ 2 characters), `sort`. `signal` lets a newer search or an
   * unmounted rail cancel a request that no longer matters.
   */
  titles(
    opts: {
      page?: number;
      pageSize?: number;
      lang: Lang;
      genre?: string;
      q?: string;
      sort?: TitleSort;
    },
    signal?: AbortSignal
  ): Promise<TitlesPage> {
    return call("GET", `/titles${qs(opts)}`, undefined, {
      retry: false,
      signal,
    });
  },
  /** Genres in this portal's catalogue, most titles first. */
  genres(
    opts: { lang: Lang },
    signal?: AbortSignal
  ): Promise<{ genres: GenreCount[]; unavailable?: boolean }> {
    return call("GET", `/genres${qs({ lang: opts.lang })}`, undefined, {
      retry: false,
      signal,
    });
  },
  /** The store page's carousels, without items (each lazy-loads /titles). */
  rails(
    opts: { lang: Lang },
    signal?: AbortSignal
  ): Promise<{
    rails: StoreRail[];
    unavailable?: boolean;
    truncated?: boolean;
  }> {
    return call("GET", `/rails${qs({ lang: opts.lang })}`, undefined, {
      retry: false,
      signal,
    });
  },
  title(assetId: string, lang: Lang): Promise<TitleDetail> {
    return call(
      "GET",
      `/titles/${encodeURIComponent(assetId)}${qs({ lang })}`,
      undefined,
      noRetry
    );
  },

  /* ── Mitt innehåll ── */

  myTitles(lang: Lang): Promise<MyTitles> {
    return call("GET", `/me/titles${qs({ lang })}`);
  },

  /* ── Min lista (slice J) ── */

  myList(lang: Lang): Promise<{ items: ListItem[] }> {
    return call("GET", `/me/list${qs({ lang })}`);
  },
  addToList(assetId: string): Promise<{ onMyList: boolean }> {
    return call("PUT", `/me/list/${encodeURIComponent(assetId)}`);
  },
  removeFromList(assetId: string): Promise<{ onMyList: boolean }> {
    return call("DELETE", `/me/list/${encodeURIComponent(assetId)}`);
  },

  /* ── playback (the play host; docs/design/web-streaming-app.md §3.3) ── */

  /** Open the member's one play session and get the stream. */
  play(assetId: string, deviceId: string): Promise<WebPlayResponse> {
    return call("POST", `/play/${encodeURIComponent(assetId)}`, { deviceId });
  },
  heartbeat(
    playSessionId: string,
    positionSeconds?: number
  ): Promise<WebHeartbeatResponse> {
    return call(
      "POST",
      `/play/${encodeURIComponent(playSessionId)}/heartbeat`,
      positionSeconds === undefined ? {} : { positionSeconds }
    );
  },
  /** A fresh licence for an open session - on EVERY licence request. */
  licence(playSessionId: string): Promise<WebLicenceResponse> {
    return call("POST", `/play/${encodeURIComponent(playSessionId)}/licence`);
  },
  /**
   * End the play session. `keepalive` so it still reaches the server when the
   * tab is closing; no refresh-and-retry (a dead session has nothing to stop).
   */
  stop(playSessionId: string, positionSeconds?: number): void {
    void fetch(`${BASE}/play/${encodeURIComponent(playSessionId)}/stop`, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(
        positionSeconds === undefined ? {} : { positionSeconds }
      ),
    }).catch(() => undefined);
  },
  episodes(assetId: string, lang: Lang): Promise<WebEpisodesResponse> {
    return call(
      "GET",
      `/titles/${encodeURIComponent(assetId)}/episodes${qs({ lang })}`
    );
  },

  /* ── schools ── */

  acceptInvite(body: {
    token: string;
    password: string;
    name?: string;
  }): Promise<{ ok: true; email: string }> {
    return call("POST", "/invites/accept", body, noRetry);
  },
};

export type PortalApi = typeof portalApi;
